/**
 * The calls the OPTIMA panel makes — the ONLY file in the feature that names an operation
 * (bata-platform epic 719, ADR 9080; the request intake of bata-platform ADR 2137, ADR 9071).
 *
 * All three are the generated operations, pinned with the platform's contract 0.101 (`assistantStatus`,
 * `ask`, and `fileTicket` for `POST /assistant/conversations/{conversation_id}/tickets`); the
 * transport gives them the base URL, the token, the language, the write-time `Idempotency-Key` and
 * the RFC 9457 parse. Nothing here calls `fetch`. The names below are the ones the feature has
 * always used, so nothing else in `src/app/optima/` knows the wire names.
 */

import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BACKGROUND_READ, useApiCommand, useApiQuery } from '../../api';
import { ask, assistantStatus, fileTicket, listMyReports, myWorkCounts } from '../../api/generated/operations';
import type {
  AssistantAction, AssistantLocale, AssistantStatus, CitedEntry, ClientContext, FilingOutcome, Intake, IntakeConfirmation,
  IntakeDraft, RequestKind, ScreenContext, TurnResponse,
} from '../../api/generated/types';

export type { AssistantAction, AssistantStatus, IntakeConfirmation, IntakeDraft, RequestKind };
export type AssistantCitation = CitedEntry;
export type AssistantTurn = TurnResponse;
export type TurnIntake = Intake;
export type FilingClientContext = ClientContext;
export type FilingReceipt = FilingOutcome;

/**
 * What the panel sends. The wire narrows `locale` to the three the application speaks; the
 * panel holds `i18n.language`, which i18next already restricts to exactly those three
 * (`SUPPORTED_LOCALES`), so it stays a string here and is narrowed once, below.
 */
export interface AssistantTurnRequest {
  readonly question: string;
  readonly locale: string;
  readonly context: ScreenContext;
  /** The conversation this question continues; absent for the first question. */
  readonly conversationId?: string;
}

/** The request's bound, restated from the contract (`TurnRequest.question.maxLength`) so the input can stop a person at the limit. */
export const ASSISTANT_LIMITS = {
  question: { max: 2000 },
} as const;

/** How long «is the assistant on?» counts as fresh. It changes with a deployment, not with a click. */
const STATUS_FRESH_MS = 5 * 60_000;

/**
 * Whether the assistant is on. A background read: nobody waits for it, and a platform that does
 * not carry the route (404), refuses it or cannot be reached leaves the panel hidden rather than
 * drawing an error on every screen of the application.
 */
export function useAssistantStatus(): UseQueryResult<AssistantStatus, Error> {
  return useApiQuery(assistantStatus, {}, { staleTime: STATUS_FRESH_MS, ...BACKGROUND_READ });
}

/**
 * Asks one question. Rejects with the transport's `ApiProblemError` (429 quota, 503 provider,
 * 404 disabled, 4xx the platform explained) or `ApiUnreachableError`. The idempotency key is held
 * per question, so pressing again after a dropped connection is one turn and not two.
 */
export function useAskAssistant(): (request: AssistantTurnRequest) => Promise<AssistantTurn> {
  const send = useApiCommand(ask);
  return useCallback(({ conversationId, ...request }) => send({
    body: {
      question: request.question,
      locale: request.locale as AssistantLocale,
      context: request.context,
      ...(conversationId === undefined ? {} : { conversation_id: conversationId }),
    },
  }), [send]);
}

/** What the panel sends to file a confirmed draft. */
export interface FilingCommand {
  readonly conversationId: string;
  readonly draftDigest: string;
  readonly confirmation: string;
  readonly context: FilingClientContext;
}

/**
 * Files the draft the person confirmed by pressing «Sì, invia la segnalazione». The body carries the
 * single-use token and the digest of the exact draft drawn, so the idempotency key — held per
 * decision, compared by value — is one per draft revision: a double press is one report, and a new
 * revision is a new decision. Rejects with 409 when the token is used, expired or superseded, 422
 * when the platform's rubric is not met, 429 on the report quota.
 */
export function useFileDraft(): (command: FilingCommand) => Promise<FilingReceipt> {
  const send = useApiCommand(fileTicket);
  const client = useQueryClient();
  return useCallback(async ({ conversationId, draftDigest, confirmation, context }) => {
    const receipt = await send({
      path: { conversation_id: conversationId },
      body: { draft_digest: draftDigest, confirmation, client_context: context },
    });
    // «My reports» and the count beside it show the new report.
    await Promise.all([
      client.invalidateQueries({ queryKey: [listMyReports.operationId] }),
      client.invalidateQueries({ queryKey: [myWorkCounts.operationId] }),
    ]);
    return receipt;
  }, [send, client]);
}
